import { Avatar, Box, Button, Divider, Grid, Icon, IconButton, Modal, Stack, Typography, useMediaQuery, useTheme, Alert } from "@mui/material";
import { format } from "date-fns";
import { VForm, VTextField } from "../../../../shared/forms";
import { FormHandles } from "@unform/core";
import { useEffect, useRef, useState } from "react";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import CloseIcon from "@mui/icons-material/Close";
import * as yup from "yup";
import { SinisterService } from "../../../../shared/services/api/sinister/SinisterService";
import { useToast } from "../../../../shared/hooks";
import { globalRemoveMask } from "../../../../shared/utils";
import { ToastContent } from "../../../../shared/components";

interface ModalApprovedProps {
	open: boolean;
	rowSelect: any;
	onClose: () => void;
	onSuccess: () => void;
}

export const ModalApproved = ({ open, rowSelect, onClose, onSuccess }: ModalApprovedProps) => {
	const formRef = useRef<FormHandles>(null);
	const theme = useTheme();
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));
	const toastHandler = useToast();
	const [isLoading, setIsLoading] = useState(true);
	const [alertMessage, setAlertMessage] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setIsLoading(true);
			setAlertMessage(null);

			const timeout = setTimeout(() => {
				setIsLoading(false);

				const originalValue = Number(rowSelect?.value ?? 0);
				formRef.current?.setFieldValue(
					"valueApproved",
					originalValue.toLocaleString("pt-br", {
						style: "currency",
						currency: "BRL",
					})
				);
			}, 200);

			return () => clearTimeout(timeout);
		}
	}, [open, rowSelect]);

	const cleanCurrencyValue = (value: string | number): number => {
		const onlyNumbers = String(value)
			.replace(/[^0-9,-]+/g, "")
			.replace(".", "")
			.replace(",", ".");
		return Number(onlyNumbers);
	};

	const handleValueApprovedBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		const value = event.target.value;
		const valueNumber = cleanCurrencyValue(value);
		const originalValue = Number(rowSelect?.value ?? 0);

		if (valueNumber > originalValue) {
			formRef.current?.setFieldValue(
				"valueApproved",
				originalValue.toLocaleString("pt-br", {
					style: "currency",
					currency: "BRL",
				})
			);
			setAlertMessage("O valor aprovado não pode ser maior que o valor original. Valor ajustado automaticamente.");
		} else if (valueNumber < 0) {
			formRef.current?.setFieldValue("valueApproved", "0,00");
			setAlertMessage("O valor aprovado não pode ser menor que zero. Valor ajustado automaticamente.");
		} else {
			setAlertMessage(null);
		}
	};

	const handleSubmit = async (data: any) => {
		try {
			const schema = yup.object().shape({
				valueApproved: yup.string().required("Informe o valor aprovado"),
			});

			await schema.validate(data, { abortEarly: false });

			const valueNumber = cleanCurrencyValue(data.valueApproved);
			const originalValue = Number(rowSelect?.value ?? 0);

			// Motivo só é obrigatório quando o valor aprovado é inferior ao original — nesse caso
			// vira uma contestação de valor, não uma aprovação simples (pedido da operação).
			if (valueNumber < originalValue && !data.reasonApproved?.trim()) {
				formRef.current?.setErrors({ reasonApproved: "Informe o motivo da contestação" });
				return;
			}

			if (valueNumber > originalValue) {
				formRef.current?.setFieldValue(
					"valueApproved",
					originalValue.toLocaleString("pt-br", {
						style: "currency",
						currency: "BRL",
					})
				);
				setAlertMessage("O valor aprovado não pode ser maior que o valor original. Valor ajustado automaticamente.");
				return;
			}

			if (valueNumber < 0) {
				formRef.current?.setFieldValue("valueApproved", "0,00");
				setAlertMessage("O valor aprovado não pode ser menor que zero. Valor ajustado automaticamente.");
				return;
			}

			const dadosForm = {
				valueApproved: globalRemoveMask(data.valueApproved),
				reasonApproved: data.reasonApproved,
			};

			await SinisterService.updateValueApproved(rowSelect?.id, dadosForm);

			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title={"Sucesso"} content={"Registro gravado com sucesso"} />,
			});

			onClose();
			onSuccess();
		} catch (err: any) {
			if (err instanceof yup.ValidationError) {
				const validationErrors: { [key: string]: string } = {};
				err.inner.forEach((error) => {
					if (error.path) validationErrors[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErrors);
			} else {
				toastHandler.present({
					type: "error",
					messageOrContent: err.message,
					position: "top-center",
				});
			}
		}
	};

	const InfoItem = ({ icon, label, value, isApproved }: { icon: string; label: string; value: string; isApproved?: boolean }) => (
		<Grid item xs={12} md={4}>
			<Stack direction="row" spacing={2} alignItems="flex-start">
				<Avatar sx={{ bgcolor: "#f0f0f0", width: 32, height: 32, mt: 0.5 }}>
					<Icon fontSize="small" sx={{ color: "primary.main" }}>
						{icon}
					</Icon>
				</Avatar>
				<Box>
					<Stack spacing={0.5}>
						<Typography variant="caption" color="text.secondary">
							{label}
						</Typography>
						<Typography
							variant="subtitle2"
							sx={
								isApproved
									? {
											color: "success.main",
											backgroundColor: "#d3ffe2",
											borderRadius: 2,
											px: 2,
											py: 0.3,
											display: "inline-block",
											width: "fit-content",
									  }
									: {}
							}
						>
							{value}
						</Typography>
					</Stack>
				</Box>
			</Stack>
		</Grid>
	);

	return (
		<Modal open={open} onClose={onClose}>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					bgcolor: "background.paper",
					boxShadow: 24,
					p: 4,
					width: "95%",
					maxWidth: 720,
					maxHeight: "90vh",
					overflowY: "auto",
					borderRadius: 3,
				}}
			>
				<LoadingComp isopen={isLoading} />

				<Grid container justifyContent="space-between" alignItems="center">
					<Typography variant="h4" sx={{ color: "primary.main", fontWeight: 300 }}>
						Informações da Aprovação
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</Grid>

				<Divider sx={{ my: 2 }} />

				<VForm ref={formRef} onSubmit={handleSubmit} onPointerEnterCapture={""} onPointerLeaveCapture={()=>{}} placeholder={""}>
					<Grid container direction="column" spacing={3} padding={3}>
						<Grid container spacing={2}>
							<InfoItem icon="check_circle" label="Status" value="APROVADO" isApproved />
							<InfoItem icon="calendar_month" label="Data de Aprovação" value={format(new Date(), "dd/MM/yyyy")} />
							<InfoItem
								icon="attach_money"
								label="Valor Original"
								value={parseFloat(`${rowSelect?.value ?? 0}`).toLocaleString("pt-br", {
									style: "currency",
									currency: "BRL",
								})}
							/>
						</Grid>

						{alertMessage && (
							<Alert severity="warning" sx={{ mt: 2 }}>
								{alertMessage}
							</Alert>
						)}

						<Divider sx={{ my: 2 }} />

						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<VMaskTextField
									fullWidth
									label="Valor Aprovado"
									name="valueApproved"
									disabled={false}
									mask="real"
									onBlur={handleValueApprovedBlur}
								/>
							</Grid>

							<Grid item xs={12} md={12}>
								<VTextField fullWidth label="Motivo da contestação" name="reasonApproved" disabled={false} multiline rows={4} />
								<Typography variant="caption" color="text.secondary">
									Obrigatório só quando o valor aprovado for menor que o valor original.
								</Typography>
							</Grid>
						</Grid>

						<Grid container justifyContent="flex-end" flexDirection={smDown ? "column" : "row"} gap={2} mt={3}>
							<Button color="inherit" variant="contained" startIcon={<Icon>arrow_back</Icon>} onClick={onClose} sx={{ minWidth: 140 }}>
								Voltar
							</Button>
							<Button
								color="primary"
								variant="contained"
								startIcon={<Icon>check</Icon>}
								onClick={() => formRef.current?.submitForm()}
								sx={{ minWidth: 140 }}
							>
								Confirmar
							</Button>
						</Grid>
					</Grid>
				</VForm>
			</Box>
		</Modal>
	);
};
